import type { MmObjectType } from "../api/mentalModels";
import type { CaptureKind } from "../api/capture";

// "Dive deeper?" routing (spec 2026-11-02 §5). The specific path is the
// recommendation, never forced: alternatives are always shown next to it.

export type MmScriptId = "fear_shrinker" | "belief_audit" | "decision_detox" | "identity_update";

export type DeeperRoute =
  | { route: "mm_script"; pathId: MmScriptId }
  | { route: "mm_general" }
  | { route: "need_help" }
  | { route: "project_room" }
  | { route: "trait_panel" }
  | { route: "underneath" }
  | { route: "keep" };

export interface DeeperChoice {
  recommended: DeeperRoute;
  alternatives: DeeperRoute[];
}

export interface DeeperPlan extends DeeperChoice {
  prompt: "dive_deeper" | "underneath";
  prominent: boolean;
}

/** Script ids are the keys of ClaryNext-BE utils/mmRegistry.js SCRIPTS. */
export const SCRIPT_FOR_TYPE: Partial<Record<MmObjectType, MmScriptId>> = {
  fear: "fear_shrinker",
  belief: "belief_audit",
  assumption: "belief_audit",
  decision: "decision_detox",
  self_image: "identity_update",
  value: "identity_update",
};

export function mentalModelChoice(mmType?: MmObjectType): DeeperChoice {
  const script = mmType ? SCRIPT_FOR_TYPE[mmType] : undefined;
  return script
    ? { recommended: { route: "mm_script", pathId: script }, alternatives: [{ route: "mm_general" }] }
    : { recommended: { route: "mm_general" }, alternatives: [] };
}

export function planDeeper(input: { kind: CaptureKind; mmType?: MmObjectType; reflective: boolean }): DeeperPlan {
  const { kind, mmType, reflective } = input;
  const base = { prompt: "dive_deeper" as const, prominent: reflective };
  if (reflective && (kind === "action" || kind === "insight")) {
    return { prompt: "underneath", prominent: true, recommended: { route: "underneath" }, alternatives: [{ route: "keep" }] };
  }
  if (reflective && kind === "quality") {
    return {
      prompt: "underneath",
      prominent: true,
      recommended: { route: "underneath" },
      alternatives: [{ route: "trait_panel" }, { route: "keep" }],
    };
  }
  switch (kind) {
    case "mental_model":
      return { ...base, ...mentalModelChoice(mmType) };
    case "action":
      return { ...base, recommended: { route: "need_help" }, alternatives: [] };
    case "project":
      return { ...base, recommended: { route: "project_room" }, alternatives: [] };
    case "quality":
      return { ...base, recommended: { route: "trait_panel" }, alternatives: [] };
    case "insight":
    default:
      return { ...base, recommended: { route: "underneath" }, alternatives: [{ route: "keep" }] };
  }
}

export function routeKey(r: DeeperRoute): string {
  return r.route === "mm_script" ? r.pathId : r.route;
}
